import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import BaseComponent from '../../../base.component';
import { FilterResponse } from 'src/app/shared/models/filter-response';
import { debounceTime } from 'rxjs';

@Component({
    selector: 'cdp-min-max',
    templateUrl: './min-max.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MinMaxComponent extends BaseComponent implements OnInit {
    @Input() tooltip?: string;
    @Input() controlName: string;

    private _minSelection: number;
    get minSelection(): number {
        return this._minSelection;
    }
    @Input()
    set minSelection(value: number) {
        if (value !== this._minSelection) {
            this._minSelection = value;
            this.selectedMinItemsControl.setValue(value, { emitEvent: false });
        }
    }

    private _maxSelection: number;
    get maxSelection(): number {
        return this._maxSelection;
    }
    @Input()
    set maxSelection(value: number) {
        if (value !== this._maxSelection) {
            this._maxSelection = value;
            this.selectedMaxItemsControl.setValue(value, { emitEvent: false });
        }
    }

    @Output() minSelectionChange: EventEmitter<FilterResponse<number>> = new EventEmitter<FilterResponse<number>>();
    @Output() maxSelectionChange: EventEmitter<FilterResponse<number>> = new EventEmitter<FilterResponse<number>>();

    readonly selectedMinItemsControl: FormControl = new FormControl(null);
    readonly selectedMaxItemsControl: FormControl = new FormControl(null);
    readonly minMaxSelectGroup = new FormGroup({
        selectedMinItemsControl: this.selectedMinItemsControl,
        selectedMaxItemsControl: this.selectedMaxItemsControl
    });

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.minMaxSelectGroup.valueChanges.pipe(this.unsubscribeOnDestroy()).subscribe(selection => {
            if (this.minSelection !== selection.selectedMinItemsControl) {
                const minValue: number = this.negativeValueCheck(selection.selectedMinItemsControl);
                this.minSelection = minValue;

                const minResponse: FilterResponse<number> = {
                    name: `${this.controlName}Min`,
                    selection: Number.isFinite(minValue) ? minValue : null
                };
                this.minSelectionChange.emit(minResponse);
            }

            if (this.maxSelection !== selection.selectedMaxItemsControl) {
                const maxValue: number = this.negativeValueCheck(selection.selectedMaxItemsControl);
                this.maxSelection = maxValue;

                const maxResponse: FilterResponse<number> = {
                    name: `${this.controlName}Max`,
                    selection: Number.isFinite(maxValue) ? maxValue : null
                };

                this.maxSelectionChange.emit(maxResponse);
            }
        });

        this.minMaxSelectGroup.controls['selectedMinItemsControl'].valueChanges
            .pipe(debounceTime(500), this.unsubscribeOnDestroy())
            .subscribe(() => {
                this.adjustMaxNumberRangeFilter();
            });

        this.minMaxSelectGroup.controls['selectedMaxItemsControl'].valueChanges
            .pipe(debounceTime(500), this.unsubscribeOnDestroy())
            .subscribe(() => {
                this.adjustMaxNumberRangeFilter();
            });
    }

    adjustMaxNumberRangeFilter() {
        const minControl = this.minMaxSelectGroup.controls['selectedMinItemsControl'];
        const maxControl = this.minMaxSelectGroup.controls['selectedMaxItemsControl'];

        if (!minControl.value || !maxControl.value || isNaN(maxControl.value)) {
            return;
        }

        if (maxControl.value < minControl.value) {
            maxControl.setValue(minControl.value);
        }
    }

    negativeValueCheck(value: number): number {
        return value >= 0 ? value : value * -1;
    }
}
